import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { CssBaseline } from "@mui/material";
import { themeConfig } from "./styles/theme";
import { NavMenu } from "./components";
import PostData from "./data/Posts";
import { LinksArray } from "./data/LinkObjects";
import {
  Footer,
  Hub,
  Page,
  Post,
  CopyrightPage,
  BestOf,
  BestFood,
  BestTrails,
  BestParks,
  ContactPage,
  ContactSuccessPage,
} from "./views/";
import { ContentProps, LinkProps } from "./common/types";
import { HelmetProvider } from "react-helmet-async";

// Create a context for theme toggle
const ThemeContext = React.createContext<{
  darkMode: boolean;
  toggleDarkMode: () => void;
}>({
  darkMode: false,
  toggleDarkMode: () => {},
});

function App() {
  const [darkMode, setDarkMode] = React.useState(() => {
    if (typeof window !== "undefined") {
      const stored = window.localStorage.getItem("prefersDarkMode");
      return stored ? JSON.parse(stored) : false;
    }
    return false;
  });

  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const isDarkMode = darkMode !== null ? darkMode : prefersDarkMode;

  const theme = React.useMemo(
    () => createTheme(themeConfig(isDarkMode)),
    [isDarkMode]
  );

  const toggleDarkMode = React.useCallback(() => {
    setDarkMode((prev: boolean) => {
      const newMode = !prev;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("prefersDarkMode", JSON.stringify(newMode));
      }
      return newMode;
    });
  }, []);

  return (
    <HelmetProvider>
      <ThemeContext.Provider value={{ darkMode: isDarkMode, toggleDarkMode }}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <NavMenu />

            <Routes>
              {PostData.map((post: ContentProps, i: number) => {
                return (
                  <Route
                    element={<Post currPost={post} />}
                    key={i}
                    path={`/${post.link}`}
                  />
                );
              })}

              <Route element={<BestOf />} path="/best-of" />

              <Route
                element={<BestFood />}
                path="/best-food-northwest-arkansas"
              />
              <Route
                element={<BestTrails />}
                path="/best-trails-northwest-arkansas"
              />
              <Route
                element={<BestParks />}
                path="/best-parks-northwest-arkansas"
              />

              <Route
                element={<Hub />}
                path="/best-parks-lakes-northwest-arkansas"
              />
              <Route element={<CopyrightPage />} path="/copyright" />
              <Route element={<ContactPage />} path="/contact" />
              <Route element={<ContactSuccessPage />} path="/contact-success" />

              {LinksArray.map((link: LinkProps, i: number) => {
                return (
                  <Route
                    element={<Page linkObject={link} />}
                    key={i}
                    path={`${link.link}`}
                  />
                );
              })}
            </Routes>
            <Footer />
          </Router>
        </ThemeProvider>
      </ThemeContext.Provider>
    </HelmetProvider>
  );
}

export { ThemeContext };
export default App;
